import type { DragMoveEvent } from '@dnd-kit/core'
import { Array, Order, pipe } from 'effect'

import { type FilterTreeNodeId, findLocation, type ViewConfigFilterNodeForFormWithId } from './filterTreeNode'

// DOM 属性契約: render 側（ViewFilterNodeFormItem の JSX）と geometry 側（本ファイルのクエリ）が
// 同じ DOM 要素を指すための data 属性名。属性名はここだけで定義し、
// JSX 付与は *Attribute ヘルパ、矩形取得は同じ定数から組んだセレクタを使う（名前の二重定義を防ぐ）。
const FILTER_LEAF_ID_ATTR = 'data-filter-leaf-id'
const FILTER_GROUP_ID_ATTR = 'data-filter-group-id'
const FILTER_GROUP_FRAME_ID_ATTR = 'data-filter-group-frame-id'
const FILTER_CHILDREN_END_ID_ATTR = 'data-filter-children-end-id'

/** リーフ要素に付与する data 属性（JSX に spread する）。 */
export const filterLeafIdAttribute = (id: FilterTreeNodeId): Record<typeof FILTER_LEAF_ID_ATTR, FilterTreeNodeId> => ({
  [FILTER_LEAF_ID_ATTR]: id,
})

/** グループ要素（ドラッグ対象の外枠）に付与する data 属性。 */
export const filterGroupIdAttribute = (
  id: FilterTreeNodeId,
): Record<typeof FILTER_GROUP_ID_ATTR, FilterTreeNodeId> => ({
  [FILTER_GROUP_ID_ATTR]: id,
})

/** グループの枠線要素に付与する data 属性（青線の幅・階層判定に使う）。 */
export const filterGroupFrameIdAttribute = (
  id: FilterTreeNodeId,
): Record<typeof FILTER_GROUP_FRAME_ID_ATTR, FilterTreeNodeId> => ({ [FILTER_GROUP_FRAME_ID_ATTR]: id })

/** グループの子要素末尾マーカーに付与する data 属性。 */
export const filterChildrenEndIdAttribute = (
  id: FilterTreeNodeId,
): Record<typeof FILTER_CHILDREN_END_ID_ATTR, FilterTreeNodeId> => ({ [FILTER_CHILDREN_END_ID_ATTR]: id })

// PointerSensor のみ使用しているため activatorEvent は PointerEvent（MouseEvent のサブクラス）。
// TouchSensor を導入する場合は TouchEvent 分岐の追加が必要。

/** ポインタ/マウスイベントから clientY を取り出す。 */
const getEventClientY = (event: Event): number => (event instanceof MouseEvent ? event.clientY : 0)

/** ポインタ/マウスイベントから clientX を取り出す。 */
const getEventClientX = (event: Event): number => (event instanceof MouseEvent ? event.clientX : 0)

/**
 * 現在のポインタ位置を「掴んだ瞬間のポインタ」からの相対で再構築する（スクロールに強い）。
 * grabOffset = 掴んだポインタ − ドラッグ開始時の active 左上端。
 * 現在ポインタ = 現在の active 左上端 + grabOffset。すべて viewport 座標で getBoundingClientRect と整合。
 * X は階層（横位置）判定、Y は階層内の挿入位置判定に使う。
 */
const computePointer = (event: DragMoveEvent): Pointer => {
  const { active, activatorEvent } = event
  const initialRect = active.rect.current.initial
  const translatedRect = active.rect.current.translated
  const grabOffsetX = getEventClientX(activatorEvent) - (initialRect?.left ?? 0)
  const grabOffsetY = getEventClientY(activatorEvent) - (initialRect?.top ?? 0)
  return { x: (translatedRect?.left ?? 0) + grabOffsetX, y: (translatedRect?.top ?? 0) + grabOffsetY }
}

/** id から leaf/group の DOM 矩形を引く（data 属性経由）。スロットの Y 算出に使う。 */
const getRectById = (id: FilterTreeNodeId): DOMRect | undefined => {
  const element = document.querySelector<HTMLElement>(
    // eslint-disable-next-line unicorn/require-css-escape
    `[${FILTER_LEAF_ID_ATTR}="${id}"], [${FILTER_GROUP_ID_ATTR}="${id}"]`,
  )
  return element?.getBoundingClientRect()
}

/** group の「枠線の矩形」を引く（青線の幅・階層判定に使う）。 */
export const getFrameRect = (groupId: FilterTreeNodeId): DOMRect | undefined => {
  // eslint-disable-next-line unicorn/require-css-escape
  const element = document.querySelector<HTMLElement>(`[${FILTER_GROUP_FRAME_ID_ATTR}="${groupId}"]`)
  return element?.getBoundingClientRect()
}

/** group の「子要素末尾（＝条件を追加ボタンの直上）」の矩形を引く。末尾挿入スロットの Y に使う。 */
const getChildrenEndRect = (groupId: FilterTreeNodeId): DOMRect | undefined => {
  // eslint-disable-next-line unicorn/require-css-escape
  const element = document.querySelector<HTMLElement>(`[${FILTER_CHILDREN_END_ID_ATTR}="${groupId}"]`)
  return element?.getBoundingClientRect()
}

/** rect の縦中心が pointerY より上か。挿入インデックスの数え上げに使う。 */
const isCenterAbove = (rect: DOMRect | undefined, pointerY: number): boolean =>
  rect !== undefined && (rect.top + rect.bottom) / 2 < pointerY

interface Pointer {
  x: number
  y: number
}

interface Candidate {
  node: ViewConfigFilterNodeForFormWithId
  frame: DOMRect
  depth: number
}

/** ポインタを内包する枠のうち最も深いグループ（無ければ最上位）を返す。group ドラッグ中は自身の subtree を除外。 */
const findTargetLevel = (
  root: ViewConfigFilterNodeForFormWithId,
  activeId: FilterTreeNodeId,
  activeKind: 'leaf' | 'group',
  pointer: Pointer,
): { node: ViewConfigFilterNodeForFormWithId; frame: DOMRect } | undefined => {
  const collect = (node: ViewConfigFilterNodeForFormWithId, depth: number, inActiveSubtree: boolean): Candidate[] => {
    const frame = inActiveSubtree ? undefined : getFrameRect(node._id)
    const self: Candidate[] = frame === undefined ? [] : [{ node, frame, depth }]
    const fromChildren = node.children.flatMap((child) =>
      collect(child, depth + 1, inActiveSubtree || (activeKind === 'group' && child._id === activeId)),
    )
    return [...self, ...fromChildren]
  }
  const candidates = collect(root, 0, false)

  const containing = candidates.filter(
    (c) =>
      pointer.x >= c.frame.left &&
      pointer.x <= c.frame.right &&
      pointer.y >= c.frame.top &&
      pointer.y <= c.frame.bottom,
  )
  if (Array.isReadonlyArrayNonEmpty(containing)) {
    // 最も深い（depth が最大の）グループを選ぶ
    return Array.max(
      containing,
      Order.mapInput(Order.Number, (candidate: Candidate) => candidate.depth),
    )
  }
  return candidates.find((c) => c.node._id === root._id)
}

/** グループの枠線に青線が重ならないよう、グループ境界に出すときに少し離す量(px)。 */
const GROUP_INDICATOR_GAP = 10

interface ComputeSlotParameters {
  node: ViewConfigFilterNodeForFormWithId
  frame: DOMRect
  activeId: FilterTreeNodeId
  pointer: Pointer
}

/** 挿入スロット：どの親の leafs/children 配列の何番目に挿すか、画面上の Y、および親の枠の左端/幅。 */
interface DropSlot {
  parentId: FilterTreeNodeId
  index: number
  y: number
  frameLeft: number
  frameWidth: number
}

/** リーフドラッグ時の挿入スロット。ドラッグ中の自身は除外して数える（変形後矩形による index ズレを防ぐ）。 */
const computeLeafSlot = ({ node, frame, activeId, pointer }: ComputeSlotParameters): DropSlot => {
  const base = { parentId: node._id, frameLeft: frame.left, frameWidth: frame.width }
  const rects = pipe(
    node.leafs,
    Array.filter((leaf) => leaf._id !== activeId),
    Array.map((leaf) => getRectById(leaf._id)),
  )
  const index = rects.filter((rect) => isCenterAbove(rect, pointer.y)).length
  if (index < rects.length) {
    return { ...base, index, y: rects.at(index)?.top ?? frame.top }
  }
  // リーフ領域より下（＝子グループ領域）にポインタがある場合。データ上の移動先は「この階層の leafs 末尾」で
  // 一意に決まる（リーフは常にグループより上に描画されるため）。
  // ただし青線は「どのグループの間に落ちるか」が分かるよう、各子グループの隙間（上端／最後の下端）の
  // 最近傍にスナップして表示する（グループが多くても位置が分かる。連続追従はしない）。
  // 各境界はグループ枠線と重ならないよう GROUP_INDICATOR_GAP だけ離す（上端は上へ、最後の下端は下へ）。
  const leavesEndY = rects.length > 0 ? (rects.at(-1)?.bottom ?? frame.top) : frame.top
  const childTops = Array.flatMapNullishOr(node.children, (child) => {
    const rect = getRectById(child._id)
    return rect === undefined ? undefined : rect.top - GROUP_INDICATOR_GAP
  })
  const lastChild = node.children.at(-1)
  const lastChildRect = lastChild === undefined ? undefined : getRectById(lastChild._id)
  const gapYs = lastChildRect === undefined ? childTops : [...childTops, lastChildRect.bottom + GROUP_INDICATOR_GAP]
  if (!Array.isReadonlyArrayNonEmpty(gapYs)) {
    return { ...base, index, y: getChildrenEndRect(node._id)?.top ?? leavesEndY }
  }
  // pointer.y に最も近い境界（距離が最小のもの）にスナップする
  const y = Array.min(
    gapYs,
    Order.mapInput(Order.Number, (value: number) => Math.abs(value - pointer.y)),
  )
  return { ...base, index, y }
}

/** グループドラッグ時の挿入スロット。ドラッグ中の自身は除外して数える。 */
const computeGroupSlot = ({ node, frame, activeId, pointer }: ComputeSlotParameters): DropSlot => {
  const base = { parentId: node._id, frameLeft: frame.left, frameWidth: frame.width }
  const rects = pipe(
    node.children,
    Array.filter((child) => child._id !== activeId),
    Array.map((child) => getRectById(child._id)),
  )
  const index = rects.filter((rect) => isCenterAbove(rect, pointer.y)).length
  if (index < rects.length) {
    // 「グループの前」に挿入する位置。そのグループ枠の上端ちょうどだと枠線と重なるので少し上に離す。
    const top = rects.at(index)?.top ?? frame.top
    return { ...base, index, y: top - GROUP_INDICATOR_GAP }
  }
  // 「最後のグループの後ろ」。最後のグループ下端ちょうどだと枠線と重なるので少し下に離す。
  const lastBottom = rects.at(-1)?.bottom
  const y =
    lastBottom === undefined ? (getChildrenEndRect(node._id)?.top ?? frame.bottom) : lastBottom + GROUP_INDICATOR_GAP
  return { ...base, index, y }
}

/**
 * 移動先スロットを「ポインタが含まれる一番深いグループ枠＝対象階層」「その階層内で pointer.y により挿入位置」で決める。
 * 横に動かさない限り対象階層は変わらず（途中で別グループ内に化けない）、縦は単調に位置が動く。
 */
const findTargetSlot = (
  root: ViewConfigFilterNodeForFormWithId,
  activeId: FilterTreeNodeId,
  activeKind: 'leaf' | 'group',
  pointer: Pointer,
): DropSlot | undefined => {
  const level = findTargetLevel(root, activeId, activeKind, pointer)
  if (level === undefined) return undefined
  const parameters = { node: level.node, frame: level.frame, activeId, pointer }
  return activeKind === 'leaf' ? computeLeafSlot(parameters) : computeGroupSlot(parameters)
}

/**
 * ツリー全体を DOM 表示順にフラット化した id 列を返す。
 * SortableContext には全 sortable id をルートで一度だけ登録する方針なので、その入力に使う。
 * 順序：[親leafs..., 各子グループID, 子グループ内leafs..., その孫グループID, ...]
 */
export const collectAllSortableIds = (node: ViewConfigFilterNodeForFormWithId): FilterTreeNodeId[] => [
  ...node.leafs.map((leaf) => leaf._id),
  ...node.children.flatMap((child) => [child._id, ...collectAllSortableIds(child)]),
]

interface DragContext {
  activeId: FilterTreeNodeId
  activeKind: 'leaf' | 'group'
  source: { parentId: FilterTreeNodeId; index: number }
  slot: DropSlot
}

/**
 * ドラッグ対象の解決：active から「種別・移動元・挿入スロット」を求める（onDragEnd / onDragMove 共通の前半）。
 * 対象が見つからない / ルート自体 / スロット算出不能なら undefined。移動先は over でなくポインタ位置から決める。
 */
export const resolveDragContext = (
  event: DragMoveEvent,
  rootNode: ViewConfigFilterNodeForFormWithId,
): DragContext | undefined => {
  const activeId = String(event.active.id)
  const activeLocation = findLocation(rootNode, activeId)
  // ルートグループ自体は動かせない（group で parent が無いのはルートのみ）
  const sourceParent = activeLocation?.parent
  if (activeLocation === undefined || sourceParent === undefined) return undefined
  const slot = findTargetSlot(rootNode, activeId, activeLocation.kind, computePointer(event))
  if (slot === undefined) return undefined
  const source = { parentId: sourceParent._id, index: activeLocation.index }
  return { activeId, activeKind: activeLocation.kind, source, slot }
}
